import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Public live donations board, ported from the Laravel site's /donations/{fundraiser} page
// (resources/js/components/donations/transaction-list.vue). Coaches and students keep it open
// during a fundraiser: it refreshes every 10 seconds, plays a sound on new donations and fires
// confetti every $1,000.
const money = (value) => (value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const amount = (value) => (value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pad = (value) => (value < 10 ? `0${value}` : `${value}`);
const ordinal = (day) => {
    if (day > 3 && day < 21) return `${day}th`;
    return day + (['th', 'st', 'nd', 'rd'][day % 10] || 'th');
};
// Same format as moment's 'MMMM Do YYYY, h:mm a' on the Laravel page.
const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    const month = d.toLocaleString('en-US', { month: 'long' });
    const hours = d.getHours() % 12 || 12;
    return `${month} ${ordinal(d.getDate())} ${d.getFullYear()}, ${hours}:${pad(d.getMinutes())} ${d.getHours() < 12 ? 'am' : 'pm'}`;
};

// Bootstrap 5 (loaded app-wide) draws a heavy rule under table headers that the Laravel page didn't have.
const noHeaderRule = { borderTop: 0 };
const CONFETTI_SCRIPT = 'https://cdn.jsdelivr.net/npm/js-confetti@0.12.0/dist/js-confetti.browser.js';
// Laravel's pages loaded Nunito Sans; the Orders app doesn't, so this page adds it.
const NUNITO_SANS = 'https://fonts.googleapis.com/css?family=Nunito+Sans:300,400,400i,600,700';
const COUNTDOWN_SECONDS = 3600;

// Page list the way laravel-vue-pagination (limit 3) built it: first, last and current±3, with '...' for gaps.
const pageList = (current, last) => {
    const pages = [];
    let previous = null;
    for (let i = 1; i <= last; i++) {
        if (i !== 1 && i !== last && (i < current - 3 || i >= current + 4)) continue;
        if (previous !== null) {
            if (i - previous === 2) pages.push(previous + 1);
            else if (i - previous !== 1) pages.push('...');
        }
        pages.push(i);
        previous = i;
    }
    return pages;
};

export default function Donations() {
    const { fundraiserId } = useParams();
    const [board, setBoard] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [search, setSearch] = useState({ query: '', orderBy: 'id', direction: 'DESC', page: 1 });
    const [searchText, setSearchText] = useState('');
    const [participantOrder, setParticipantOrder] = useState({ field: 'first_name', type: 'alpha', ascending: true });
    const [confettiEnabled, setConfettiEnabled] = useState(true);
    const [countDown, setCountDown] = useState(COUNTDOWN_SECONDS);
    const [timerRunning, setTimerRunning] = useState(false);

    const searchRef = useRef(search);
    const confettiEnabledRef = useRef(confettiEnabled);
    const lastCountRef = useRef(null);
    const lastGrandTotalRef = useRef(0);
    const isSearchRef = useRef(false);
    const debounceRef = useRef(null);

    useEffect(() => { searchRef.current = search; }, [search]);
    useEffect(() => { confettiEnabledRef.current = confettiEnabled; }, [confettiEnabled]);

    useEffect(() => {
        if (!document.querySelector(`link[href="${NUNITO_SANS}"]`)) {
            const font = document.createElement('link');
            font.rel = 'stylesheet';
            font.href = NUNITO_SANS;
            document.head.appendChild(font);
        }
        if (window.JSConfetti || document.querySelector(`script[src="${CONFETTI_SCRIPT}"]`)) return;
        const script = document.createElement('script');
        script.src = CONFETTI_SCRIPT;
        document.body.appendChild(script);
    }, []);

    const load = useCallback(() => {
        const params = searchRef.current;
        axios.get(`/api/Donations/Board/${fundraiserId}`, {
            params: { query: params.query, orderBy: params.orderBy, direction: params.direction, page: params.page }
        }).then(res => {
            // Ignore answers to a search, sort or page the board has already moved past.
            if (params !== searchRef.current) return;
            const data = res.data;
            if (lastCountRef.current !== null && data.total > lastCountRef.current && !isSearchRef.current) {
                new Audio('/media/cha-ching.mp3').play().catch(() => { });
            }
            if (confettiEnabledRef.current && data.grandTotal - lastGrandTotalRef.current >= 1000 && window.JSConfetti) {
                new window.JSConfetti().addConfetti();
            }
            isSearchRef.current = false;
            lastCountRef.current = data.total;
            lastGrandTotalRef.current = data.grandTotal;
            setBoard(data);
        }).catch(error => {
            if (error.response && error.response.status === 404) setNotFound(true);
        });
    }, [fundraiserId]);

    useEffect(() => { load(); }, [search, load]);
    useEffect(() => {
        const interval = setInterval(load, 10000);
        return () => clearInterval(interval);
    }, [load]);

    // Like Laravel, the countdown stops on 00:00:00 and only Reset starts a new hour.
    const timerExpired = countDown === 0;
    useEffect(() => {
        if (!timerRunning || timerExpired) return undefined;
        const tick = setInterval(() => setCountDown(value => Math.max(0, value - 1)), 1000);
        return () => clearInterval(tick);
    }, [timerRunning, timerExpired]);

    const commitSearch = (query) => {
        clearTimeout(debounceRef.current);
        isSearchRef.current = true;
        setSearch(s => ({ ...s, query, page: 1 }));
    };
    const onSearchChange = (event) => {
        const query = event.target.value;
        setSearchText(query);
        clearTimeout(debounceRef.current);
        isSearchRef.current = true;
        debounceRef.current = setTimeout(() => commitSearch(query), 300);
    };
    const orderBy = (field) => setSearch(s => ({ ...s, orderBy: field, direction: s.direction === 'DESC' ? 'ASC' : 'DESC', page: 1 }));
    const changePage = (page) => (event) => {
        event.preventDefault();
        setSearch(s => ({ ...s, page }));
    };
    const setParticipantOrderBy = (field, type) => setParticipantOrder(o => ({ field, type, ascending: !o.ascending }));
    const resetCount = () => {
        setCountDown(COUNTDOWN_SECONDS);
        setTimerRunning(true);
    };

    if (notFound) {
        return <div className="content">This fundraiser could not be found.</div>;
    }

    const isFreshTimer = countDown === COUNTDOWN_SECONDS;
    const timer = {
        hour: isFreshTimer ? '01' : '00',
        min: isFreshTimer ? '00' : pad(Math.trunc(countDown / 60)),
        seg: isFreshTimer ? '00' : pad(countDown % 60),
    };

    const participants = [...(board ? board.participants : [])];
    participants.sort((a, b) => {
        const field = participantOrder.field === 'first_name' ? 'firstName'
            : participantOrder.field === 'last_name' ? 'lastName'
                : participantOrder.field === 'total_amount' ? 'totalAmount' : participantOrder.field;
        if (participantOrder.type === 'alpha') {
            const fa = (a[field] || '').toLowerCase();
            const fb = (b[field] || '').toLowerCase();
            return fa < fb ? -1 : fa > fb ? 1 : 0;
        }
        return a[field] - b[field];
    });
    if (!participantOrder.ascending) participants.reverse();

    const currentPage = board ? board.currentPage : 1;
    const lastPage = board ? board.lastPage : 1;

    return (
        <div id="page-container" className="main-content-boxed">
            <main id="main-container">
                <div className="content">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex justify-content-start align-items-center">
                            <a href="/">
                                <img height="100px" src="/media/logo/logo-classic.png" alt="Fundraising U" />
                            </a>
                            <div className="ml-2">
                                <h1 className="h5 font-w700 mt-30 mb-10">Welcome to Fundraising U</h1>
                                <h2 className="h6 font-w400 text-muted mb-0">America's Leading Fundraising Program For High School
                                    Sports</h2>
                            </div>
                        </div>
                        <div>
                            <button type="button" className="btn p-0 mr-2" onClick={() => setConfettiEnabled(!confettiEnabled)}>
                                <img src="/media/favicons/confetti_icon.svg" alt=""
                                    title={`Turn ${confettiEnabled ? 'Off' : 'On'} Confetti`}
                                    style={{ width: '30px', opacity: confettiEnabled ? 1 : 0.5, filter: confettiEnabled ? 'none' : 'grayscale(1)' }} />
                                <p className="p-0 m-0">Confetti On/Off</p>
                            </button>
                            {' '}<span className="h5 font-w700 p-3 bg-black text-white rounded">{timer.hour}</span>
                            {' '}<span className="h5 font-weight-bolder">:</span>
                            {' '}<span className="h5 font-w700 p-3 bg-black text-white rounded">{timer.min}</span>
                            {' '}<span className="h5 font-weight-bolder">:</span>
                            {' '}<span className="h5 font-w700 p-3 bg-black text-white rounded">{timer.seg}</span>
                            <div className="text-right">
                                {!timerRunning
                                    ? <button className="btn" onClick={() => setTimerRunning(true)}><i className="fa fa-play mr-1"></i>Start</button>
                                    : <button className="btn" onClick={() => setTimerRunning(false)}><i className="fa fa-stop mr-1"></i>Stop</button>}
                                <button className="btn" onClick={resetCount}><i className="fa fa-undo mr-1"></i>Reset</button>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-light">
                        <div className="content">
                            <div className="row push mb-0">
                                <div className="col-md d-md-flex align-items-md-center text-center pl-0">
                                    <h1 className="text-white">
                                        <span className="font-w300 font-size-h4 text-corporate-dark font-weight-bold">Donations</span>
                                    </h1>
                                </div>
                                <div className="col-md d-md-flex align-items-md-center justify-content-md-end text-center pr-0">
                                    <form onSubmit={(e) => { e.preventDefault(); commitSearch(searchText); }} method="GET" autoComplete="off" className="w-100">
                                        <div className="form-group">
                                            <div className="input-group">
                                                <input type="text" className="form-control" name="query" value={searchText} onChange={onSearchChange} placeholder="Search.." />
                                                <div className="input-group-append">
                                                    <button type="submit" className="btn btn-light">
                                                        <i className="fal fa-search"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div className="col-1 text-right">
                                    <h5 className="pb-0 mb-0">TOTAL</h5>
                                    <label>${amount(board && board.grandTotal)}</label>
                                </div>
                                <div className="col-1 text-right">
                                    <h5 className="pb-0 mb-0">AVG</h5>
                                    <label>${amount(board && board.average)}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="content px-0">
                        <div className="border-bottom">
                            <p className="h5 mb-1">Fundraiser</p>
                            <p className="h6">{board && board.fundraiserName}</p>
                        </div>
                        <div className="block-content p-0">
                            <div className="table table-clickable-rows table-hover table-responsive">
                                <table className="table border-bottom sortable dataTable">
                                    <thead>
                                        <tr>
                                            <th>Datetime <i className="fas fa-sort" onClick={() => orderBy('created_at')}></i></th>
                                            <th>First Name <i className="fas fa-sort" onClick={() => orderBy('first_name')}></i></th>
                                            <th>Last Name <i className="fas fa-sort" onClick={() => orderBy('last_name')}></i></th>
                                            <th>Amount <i className="fas fa-sort" onClick={() => orderBy('order_total')}></i></th>
                                            <th>Supporter <i className="fas fa-sort" onClick={() => orderBy('supporter')}></i></th>
                                        </tr>
                                    </thead>
                                    <tbody role="button" style={noHeaderRule}>
                                        {board && board.donations.map(donation => (
                                            <tr key={`donation-${donation.id}`}>
                                                <td>{formatDate(donation.createdAt)}</td>
                                                <td>{donation.studentFirstName}</td>
                                                <td>{donation.studentLastName}</td>
                                                <td>{money(donation.orderTotal)}</td>
                                                <td>{donation.supporter}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {lastPage > 1 && (
                                <div className="pagination pb-20">
                                    <ul className="pagination">
                                        {currentPage > 1 && (
                                            <li className="page-item"><button type="button" className="page-link" onClick={changePage(currentPage - 1)}>&laquo;</button></li>
                                        )}
                                        {pageList(currentPage, lastPage).map((p, i) => p === '...' ? (
                                            <li key={`gap-${i}`} className="page-item"><span className="page-link">...</span></li>
                                        ) : (
                                            <li key={p} className={`page-item${p === currentPage ? ' active' : ''}`}>
                                                <button type="button" className="page-link" onClick={changePage(p)}>{p}</button>
                                            </li>
                                        ))}
                                        {currentPage < lastPage && (
                                            <li className="page-item"><button type="button" className="page-link" onClick={changePage(currentPage + 1)}>&raquo;</button></li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="block-content p-0">
                            <div className="col-md d-md-flex align-items-md-center text-center pl-0">
                                <h1 className="text-white">
                                    <span className="font-w300 font-size-h4 text-corporate-dark font-weight-bold">Participant Summary</span>
                                </h1>
                            </div>
                            <div className="table table-clickable-rows table-hover table-responsive">
                                <table className="table border-bottom">
                                    <thead>
                                        <tr>
                                            <th>First Name <i className="fas fa-sort" onClick={() => setParticipantOrderBy('first_name', 'alpha')}></i></th>
                                            <th>Last Name <i className="fas fa-sort" onClick={() => setParticipantOrderBy('last_name', 'alpha')}></i></th>
                                            <th># of Donations <i className="fas fa-sort" onClick={() => setParticipantOrderBy('donations', '')}></i></th>
                                            <th>Total Donations <i className="fas fa-sort" onClick={() => setParticipantOrderBy('total_amount', '')}></i></th>
                                            <th>Shares <i className="fas fa-sort" onClick={() => setParticipantOrderBy('shares', '')}></i></th>
                                            <th>Visits <i className="fas fa-sort" onClick={() => setParticipantOrderBy('visits', '')}></i></th>
                                        </tr>
                                    </thead>
                                    <tbody role="button" style={noHeaderRule}>
                                        {participants.map((participant, k) => (
                                            <tr key={`participant-${k}`}>
                                                <td>{participant.firstName}</td>
                                                <td>{participant.lastName}</td>
                                                <td>{participant.donations}</td>
                                                <td>{money(participant.totalAmount)}</td>
                                                <td>{participant.shares || 0}</td>
                                                <td>{participant.visits || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
